import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type {
  DfyEditorVideoRendersScope,
  DfyOrderSort,
  DfyOrderStatus,
} from "@repo/api/dfy";
import { DfyApi } from "../api/dfy-api";

export const dfyQueryKeys = {
  customerOrder: (teamExternalId?: string, orderExternalId?: string) => [
    "dfy",
    "order",
    teamExternalId,
    orderExternalId,
  ],
  editorOrder: (teamExternalId?: string, orderExternalId?: string) => [
    "dfy",
    "editor-order",
    teamExternalId,
    orderExternalId,
  ],
  editorRenders: (
    teamExternalId: string,
    orderExternalId: string,
    scope: DfyEditorVideoRendersScope,
  ) => ["dfy", "editor-renders", teamExternalId, orderExternalId, scope],
  customerOrders: (teamExternalId: string) => ["dfy", "orders", teamExternalId],
  editorOrders: (
    teamExternalId: string,
    sort: DfyOrderSort,
    status?: DfyOrderStatus,
    search?: string,
  ) => ["dfy", "editor-orders", teamExternalId, { sort, status, search }],
};

export function useDfyOrderQuery(
  teamExternalId?: string,
  orderExternalId?: string,
) {
  return useQuery({
    queryKey: dfyQueryKeys.customerOrder(teamExternalId, orderExternalId),
    queryFn: () => DfyApi.getOrder(teamExternalId!, orderExternalId!),
    enabled: !!teamExternalId && !!orderExternalId,
  });
}

export function useDfyEditorOrderQuery(
  teamExternalId?: string,
  orderExternalId?: string,
) {
  return useQuery({
    queryKey: dfyQueryKeys.editorOrder(teamExternalId, orderExternalId),
    queryFn: () => DfyApi.getOrder(teamExternalId!, orderExternalId!),
    enabled: !!teamExternalId && !!orderExternalId,
  });
}

export function usePagedDfyEditorVideoRendersQuery(
  teamExternalId: string,
  orderExternalId: string,
  scope: DfyEditorVideoRendersScope,
) {
  return useInfiniteQuery({
    queryKey: dfyQueryKeys.editorRenders(
      teamExternalId,
      orderExternalId,
      scope,
    ),
    queryFn: ({ pageParam }) =>
      DfyApi.listEditorVideoRenders(teamExternalId, orderExternalId, {
        page: pageParam,
        scope,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextPage,
  });
}

export function useDfyOrdersQuery(teamExternalId: string) {
  return useQuery({
    queryKey: dfyQueryKeys.customerOrders(teamExternalId),
    queryFn: () => DfyApi.listCustomerOrders(teamExternalId),
    enabled: !!teamExternalId,
  });
}

export function usePagedDfyEditorOrdersQuery(
  teamExternalId: string,
  sort: DfyOrderSort = "newest",
  status?: DfyOrderStatus,
  search?: string,
) {
  return useInfiniteQuery({
    queryKey: dfyQueryKeys.editorOrders(teamExternalId, sort, status, search),
    queryFn: ({ pageParam }) =>
      DfyApi.listEditorOrders(teamExternalId, {
        page: pageParam,
        sort,
        status,
        search,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!teamExternalId,
  });
}
